import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { lookupGroupByInviteCode, joinGroup } from '@/lib/supabase-queries';
import { useToast } from '@/hooks/use-toast';
import Layout from '@/components/Layout';
import { trackEvent } from '@/lib/analytics';

const JoinGroup = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { user, displayName, avatarId } = useAuth();
  const { toast } = useToast();

  const [code, setCode] = useState(inviteCode || '');
  const [group, setGroup] = useState<any>(null);
  const [loading, setLoading] = useState(!!inviteCode);
  const [joining, setJoining] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const lookup = async (c: string) => {
    if (!c.trim()) return;
    setLoading(true);
    setNotFound(false);
    const g = await lookupGroupByInviteCode(c.trim().toUpperCase());
    if (g) {
      setGroup(g);
      localStorage.removeItem('pendingInvitePath');
    } else {
      setNotFound(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (inviteCode) lookup(inviteCode);
  }, [inviteCode]);

  const handleJoin = async () => {
    if (!user || !group) return;
    setJoining(true);
    try {
      const { alreadyMember } = await joinGroup(group.id, user.id, displayName || 'משתמש', avatarId);
      if (alreadyMember) {
        toast({ title: 'כבר חבר בקבוצה', description: 'מעביר אותך לקבוצה...' });
      } else {
        toast({ title: 'הצטרפת לקבוצה! 🎉', description: `ברוכים הבאים ל${group.name}` });
        trackEvent('group_joined', { groupId: group.id });
        localStorage.setItem('br_ios_install_trigger', 'true');
      }
      localStorage.removeItem('pendingInvitePath');
      navigate(`/group/${group.id}`);
    } catch (err: any) {
      toast({ title: 'בעיית חיבור', description: 'נסה שוב מאוחר יותר', variant: 'destructive' });
    }
    setJoining(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    lookup(code);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-20 max-w-md text-center">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
          {!inviteCode && !group && (
            <div className="bg-card border border-border rounded-2xl p-8 card-elevated">
              <h2 className="text-xl font-bold mb-4">הצטרף לקבוצה</h2>
              <form onSubmit={handleSearch} className="space-y-3">
                <Input
                  placeholder="הכנס קוד הזמנה"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  className="bg-secondary border-border text-center min-h-[48px] font-mono"
                  dir="ltr"
                />
                <Button variant="accent" className="w-full min-h-[48px]" disabled={loading || !code.trim()}>
                  {loading ? 'מחפש...' : 'חפש קבוצה'}
                </Button>
              </form>
              {notFound && (
                <div className="mt-4 bg-destructive/10 border border-destructive/30 rounded-xl p-4">
                  <p className="text-destructive text-sm font-medium">קוד הזמנה לא תקין</p>
                  <p className="text-xs text-muted-foreground mt-1">בדוק שהקוד נכון ונסה שוב</p>
                  <Link to="/" className="text-xs text-accent hover:underline mt-2 inline-block">חזרה לדף הבית</Link>
                </div>
              )}
            </div>
          )}

          {loading && inviteCode && (
            <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto" />
          )}

          {group && (
            <div className="bg-card border border-border rounded-2xl p-8 card-elevated">
              <div className="w-16 h-16 mx-auto rounded-full bg-accent/10 flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-accent" />
              </div>
              <h2 className="text-xl font-bold mb-1">{group.name}</h2>
              <p className="text-muted-foreground text-sm mb-6">קוד: <span className="font-mono" dir="ltr">{group.invite_code}</span></p>
              <Button variant="accent" size="lg" className="w-full min-h-[48px]" onClick={handleJoin} disabled={joining}>
                {joining ? 'מצטרף...' : 'הצטרף לקבוצה'}
              </Button>
            </div>
          )}

          {notFound && inviteCode && (
            <div className="bg-card border border-border rounded-2xl p-8 card-elevated">
              <h2 className="text-xl font-bold mb-2">קוד לא תקין</h2>
              <p className="text-muted-foreground mb-4">לא נמצאה קבוצה עם הקוד הזה</p>
              <Link to="/"><Button variant="secondary" className="min-h-[48px]">חזרה לדף הבית</Button></Link>
            </div>
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

export default JoinGroup;
